import { clean } from "./utils/array";
import { isNumericOr } from "./utils/number";

const ENV_VARIABLES = {
  MAINTENANCE_START_UTC: import.meta.env.VITE_APP_MAINTENANCE_START_UTC as string,
  MAINTENANCE_END_UTC: import.meta.env.VITE_APP_MAINTENANCE_END_UTC as string,
  ENVIRONMENT: (import.meta.env.VITE_APP_ENVIRONMENT || "development") as string,
  APP_DOMAIN: import.meta.env.VITE_APP_DOMAIN as string,
  APP_VERSION: import.meta.env.VITE_APP_VERSION as string,
  APP_RELEASE_VERSION: import.meta.env.VITE_APP_RELEASE_VERSION as string,
  API_URL: import.meta.env.VITE_APP_API_URL as string,
  API_URL_INTELLIGENCE: import.meta.env.VITE_API_URL_INTELLIGENCE as string,
  API_URL_TAAS: import.meta.env.VITE_APP_API_URL_TAAS as string,
  API_DEFAULT_TIMEOUT: parseInt(import.meta.env.VITE_APP_API_DEFAULT_TIMEOUT) as number,
  API_DEPLOY_CLUSTER_TIMEOUT: parseInt(
    import.meta.env.VITE_APP_API_DEPLOY_CLUSTER_TIMEOUT
  ) as number,
  RECAPTCHA_SITE_KEY: import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string,
  RECAPTCHA_V2_SITE_KEY: import.meta.env.VITE_APP_RECAPTCHA_V2_SITE_KEY as string,
  PASSWORDLESS_CAPTCHA_ENABLED: import.meta.env.VITE_APP_PASSWORDLESS_CAPTCHA_ENABLED === "true",
  MAPBOX_PUBLIC_TOKEN: import.meta.env.VITE_APP_MAPBOX_PUBLIC_TOKEN as string,
  IS_PAYOUT: import.meta.env.VITE_APP_FEATUREFLAG_IS_PAYOUT === "true",
  IS_HEAD_NODES_CHECK_DISABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_IS_HEAD_NODES_CHECK_DISABLED === "true",
  INTERCOM_ENABLED: import.meta.env.VITE_APP_INTERCOM_ENABLED as string,
  BETTERSTACK_ENABLED: import.meta.env.VITE_APP_BETTERSTACK_ENABLED === "true",
  NEXTROLL_ENABLED: import.meta.env.VITE_APP_NEXTROLL_ENABLED === "true",
  SECTION_SUBDOMAINS_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_SECTION_SUBDOMAINS_ENABLED === "true",
  LOGIN_SUBDOMAIN: import.meta.env.VITE_APP_LOGIN_SUBDOMAIN as string,
  FEATUREFLAG_POINTS_ENABLED: import.meta.env.VITE_APP_FEATUREFLAG_POINTS_ENABLED === "true",
  FEATUREFLAG_POINTS_NOTICE_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_POINTS_NOTICE_ENABLED === "true",
  FEATUREFLAG_BARE_METAL_DEPLOY: import.meta.env.VITE_APP_FEATUREFLAG_BARE_METAL_DEPLOY === "true",
  FEATUREFLAG_KUBERNETES_DEPLOY: import.meta.env.VITE_APP_FEATUREFLAG_KUBERNETES_DEPLOY === "true",
  FEATUREFLAG_CONTAINER_DEPLOY: import.meta.env.VITE_APP_FEATUREFLAG_CONTAINER_DEPLOY === "true",
  FEATUREFLAG_CONTAINER_REPLICA_COUNT_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_CONTAINER_REPLICA_COUNT_ENABLED !== "false",
  /** Local-only: on log API failure, fall back to a fake live SSE stream for UI testing. */
  MOCK_CONTAINER_LOGS: import.meta.env.VITE_APP_MOCK_CONTAINER_LOGS === "true",
  /** Local-only: force every Amplitude experiment to this variant, since local has no experiment key. */
  LOCAL_EXPERIMENT_VARIANT: import.meta.env.VITE_APP_LOCAL_EXPERIMENT_VARIANT ?? "",
  FEATUREFLAG_VMAAS_DEPLOY: import.meta.env.VITE_APP_FEATUREFLAG_VMAAS_DEPLOY === "true",
  /** Pay-as-you-go billing on container/VM deploys; keep off on prod until the API bills PAYG there. */
  FEATUREFLAG_PAYG_ENABLED: import.meta.env.VITE_APP_FEATUREFLAG_PAYG_ENABLED === "true",
  FEATUREFLAG_POINTS_SEASONS_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_POINTS_SEASONS_ENABLED === "true",
  POINTS_SEASONS_LIST: import.meta.env.VITE_APP_POINTS_SEASONS_LIST.split(",") as string[],
  FEATUREFLAG_SENTRY_ANALYTICS: import.meta.env.VITE_APP_FEATUREFLAG_SENTRY_ANALYTICS === "true",
  FEATUREFLAG_CLUSTER_DEPLOYMENT_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_CLUSTER_DEPLOYMENT_ENABLED === "true",
  FEATUREFLAG_RAY_CLUSTER_CREATION_MAINTENANCE:
    import.meta.env.VITE_APP_FEATUREFLAG_RAY_CLUSTER_CREATION_MAINTENANCE === "true",
  FEATUREFLAG_AMPLITUDE_TRACKING_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_AMPLITUDE_TRACKING_ENABLED === "true",
  BETTERSTACK_SOURCE_TOKEN: import.meta.env.VITE_APP_BETTERSTACK_SOURCE_TOKEN as string,
  UNVERIFIED_DEVICE_TEXT: import.meta.env.VITE_APP_UNVERIFIED_DEVICE_TEXT as string,
  LOGIN_OPTIONS: import.meta.env.VITE_APP_LOGIN_OPTIONS
    ? import.meta.env.VITE_APP_LOGIN_OPTIONS.split(",")
    : undefined,
  LOGIN_VERSION: import.meta.env.VITE_APP_LOGIN_VERSION as string,
  SENTRY_DSN: import.meta.env.VITE_APP_SENTRY_DSN as string,
  SENTRY_TRACES_SAMPLE_RATE: parseFloat(import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE),
  SENTRY_REPLAYS_SESSION_SAMPLE_RATE: parseFloat(
    import.meta.env.VITE_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
  ),
  SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: parseFloat(
    import.meta.env.VITE_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
  ),
  IS_CYPRESS: import.meta.env.IS_CYPRESS === "true",
  DEVICE_BLOCK_REWARDS_CHECKLIST_WARNING: import.meta.env
    .VITE_APP_DEVICE_BLOCK_REWARDS_CHECKLIST_WARNING,
  MIN_CLUSTER_CREATION_TIMEOUT: import.meta.env.VITE_APP_MIN_CLUSTER_CREATION_TIMEOUT,
  CLUSTER_IDE_BUTTONS_TIMEOUT: import.meta.env.VITE_APP_CLUSTER_IDE_BUTTONS_TIMEOUT,
  AMPLITUDE_KEY: import.meta.env.VITE_APP_AMPLITUDE_KEY,
  STAKING_NETWORK: import.meta.env.VITE_APP_STAKING_NETWORK,
  STAKING_NETWORK_API_ENDPOINT: import.meta.env.VITE_APP_STAKING_NETWORK_API_ENDPOINT,
  STAKING_CONTRACT_ADDRESS: import.meta.env.VITE_APP_STAKING_CONTRACT_ADDRESS,
  STAKING_MINT_TOKEN_ADDRESS: import.meta.env.VITE_APP_STAKING_MINT_TOKEN_ADDRESS,
  STAKING_SUPER_ADMIN_PUBLIC_KEY: import.meta.env.VITE_APP_STAKING_SUPER_ADMIN_PUBLIC_KEY,
  STAKING_SUPER_ADMIN_PUBLIC_KEY_ACTIVE: import.meta.env
    .VITE_APP_STAKING_SUPER_ADMIN_PUBLIC_KEY_ACTIVE,
  STAKING_SHOW_ONLY_ONLINE_DEVICES:
    import.meta.env.VITE_APP_STAKING_SHOW_ONLY_ONLINE_DEVICES === "true",
  STAKING_UNSTAKING_ENABLED: import.meta.env.VITE_APP_STAKING_UNSTAKING_ENABLED === "true",
  STAKING_COMPUTE_UNIT_LIMIT: parseFloat(import.meta.env.VITE_APP_STAKING_COMPUTE_UNIT_LIMIT),
  STAKING_COMPUTE_UNIT_PRICE: parseFloat(import.meta.env.VITE_APP_STAKING_COMPUTE_UNIT_PRICE),
  STAKING_STAKING_ENABLED: import.meta.env.VITE_APP_STAKING_STAKING_ENABLED === "true",
  STAKING_TRANSACTIONS_ENABLED: import.meta.env.VITE_APP_STAKING_TRANSACTIONS_ENABLED === "true",
  STAKING_STAKING_DEVICE_WHITELIST: clean(
    `${import.meta.env.VITE_APP_STAKING_STAKING_DEVICE_WHITELIST}`.split(",")
  ),
  MAINTENANCE_ENABLED: import.meta.env.VITE_APP_MAINTENANCE_ENABLED === "true",
  MAINTENANCE_TITLE: import.meta.env.VITE_APP_MAINTENANCE_TITLE,
  MAINTENANCE_DESCRIPTION: import.meta.env.VITE_APP_MAINTENANCE_DESCRIPTION,
  CLUSTER_CREATION_RAY_MIN_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_RAY_MIN_HARDWARE,
    4
  ),
  CLUSTER_CREATION_RAY_MAX_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_RAY_MAX_HARDWARE,
    Number.POSITIVE_INFINITY
  ),
  CLUSTER_CREATION_BARE_METAL_MIN_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_BARE_METAL_MIN_HARDWARE,
    4
  ),
  CLUSTER_CREATION_BARE_METAL_MAX_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_BARE_MEAL_MAX_HARDWARE,
    Number.POSITIVE_INFINITY
  ),
  CLUSTER_CREATION_BARE_METAL_HOURLY_MIN_DURATION: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_BARE_METAL_HOURLY_MIN_DURATION,
    24
  ),
  CLUSTER_CREATION_BARE_METAL_DAILY_MIN_DURATION: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_BARE_METAL_DAILY_MIN_DURATION,
    1
  ),
  CLUSTER_CREATION_KUBERNETES_MIN_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_KUBERNETES_MIN_HARDWARE,
    1
  ),
  CLUSTER_CREATION_KUBERNETES_MAX_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_KUBERNETES_MAX_HARDWARE,
    Number.POSITIVE_INFINITY
  ),
  CLUSTER_CREATION_CONTAINER_MIN_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_CONTAINER_MIN_HARDWARE,
    1
  ),
  CLUSTER_CREATION_CONTAINER_MAX_HARDWARE: isNumericOr(
    import.meta.env.VITE_APP_CLUSTER_CREATION_CONTAINER_MAX_HARDWARE,
    Number.POSITIVE_INFINITY
  ),
  WARNING_BANNER_ENABLED: import.meta.env.VITE_APP_WARNING_BANNER_ENABLED === "true",
  WARNING_BANNER_DESCRIPTION: import.meta.env.VITE_APP_WARNING_BANNER_DESCRIPTION,
  EXPLORER_WARNING_BANNER_ENABLED:
    import.meta.env.VITE_APP_EXPLORER_WARNING_BANNER_ENABLED === "true",
  EXPLORER_WARNING_BANNER_DESCRIPTION: import.meta.env.VITE_APP_EXPLORER_WARNING_BANNER_DESCRIPTION,
  BLOCK_REWARD_AIRDROP_LIST: clean(
    `${import.meta.env.VITE_APP_BLOCK_REWARD_AIRDROP_LIST}`.split(",")
  ),
  SHIELD_SITE_ID: import.meta.env.VITE_SHIELD_SITE_ID,
  FRESHDESK_ENABLED: import.meta.env.VITE_APP_FRESHDESK_ENABLED === "true",
  FRESHDESK_WIDGET_ID: import.meta.env.VITE_APP_FRESHDESK_WIDGET_ID,
  LATEST_APPROVED_TERMS_VERSION: import.meta.env.VITE_APP_LATEST_APPROVED_TERMS_VERSION,
  FEATUREFLAG_EXPLORER_PUBLIC: import.meta.env.VITE_APP_FEATUREFLAG_EXPLORER_PUBLIC === "true",
  FEATUREFLAG_CO_STAKING_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_CO_STAKING_ENABLED === "true",
  FEATUREFLAG_SAFETY_MODULE_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_SAFETY_MODULE_ENABLED === "true",
  FEATUREFLAG_STAKING_DASHBOARD_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_STAKING_DASHBOARD_ENABLED === "true",
  STAKING_DASHBOARD_SECTIONS: clean(
    `${import.meta.env.VITE_APP_STAKING_DASHBOARD_SECTIONS}`.split(",")
  ),
  INTELLIGENCE_MAINTENANCE_ENABLED:
    import.meta.env.VITE_APP_INTELLIGENCE_MAINTENANCE_ENABLED === "true",
  INTELLIGENCE_MODEL_IDS_UNDER_MAINTENANCE: clean(
    `${import.meta.env.VITE_APP_INTELLIGENCE_MODEL_IDS_UNDER_MAINTENANCE}`.split(",")
  ),
  TNE_ON_CHAIN_SOL_ADDRESS: import.meta.env.VITE_APP_TNE_ON_CHAIN_SOL_ADDRESS,
  SPHERE_PAY_DEV_TOKEN: import.meta.env.VITE_APP_SPHERE_PAY_DEV_TOKEN,
  SPHERE_WEBHOOK_URL: import.meta.env.VITE_APP_SPHERE_WEBHOOK_URL,
  LOGOUT_ON_API_ERROR: import.meta.env.VITE_APP_LOGOUT_ON_API_ERROR === "true",
  FEATUREFLAG_IO_CREDITS_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_IO_CREDITS_ENABLED === "true",
  /** Local/dev testing: treat IO credit balances as $0 so card payment paths can be exercised. */
  FORCE_ZERO_CREDITS: import.meta.env.VITE_APP_FORCE_ZERO_CREDITS === "true",
  TNE_TEST_CLUSTERS: clean(`${import.meta.env.VITE_APP_TNE_TEST_CLUSTERS}`.split(",")),
  FEATUREFLAG_TRAINING_ENABLED: import.meta.env.VITE_APP_FEATUREFLAG_TRAINING_ENABLED === "true",
  HUBSPOT_FORM_GUID_TRAINING_REQUEST: import.meta.env.VITE_APP_HUBSPOT_FORM_GUID_TRAINING_REQUEST,
  FEATUREFLAG_DELAYED_LOGIN: import.meta.env.VITE_APP_FEATUREFLAG_DELAYED_LOGIN === "true",
  FEATUREFLAG_PURCHASE_CREDITS_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_PURCHASE_CREDITS_ENABLED === "true",
  FEATUREFLAG_CLUSTER_LIST_POLLING_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_CLUSTER_LIST_POLLING_ENABLED === "true",
  /** Save / load named deployment configs on VM & container create flows. */
  FEATUREFLAG_SAVED_DEPLOYMENT_CONFIGS:
    import.meta.env.VITE_APP_FEATUREFLAG_SAVED_DEPLOYMENT_CONFIGS === "true",
  VITE_APP_VERCEL_API_URL: import.meta.env.VITE_APP_VERCEL_API_URL || "",
  /** Override SSH keys API base (defaults to /api/ssh-keys). Set when io-cloud backend ships. */
  SSH_KEYS_API_URL: (import.meta.env.VITE_APP_SSH_KEYS_API_URL as string) || "",
  IS_FIAT_ENABLED: import.meta.env.VITE_APP_IS_FIAT_PAYMENT_ENABLED === "true",
  TNE_OVERRIDE_TRANSACTION_TYPE_CLUSTER_ID: import.meta.env
    .VITE_APP_TNE_OVERRIDE_TRANSACTION_TYPE_CLUSTER_ID as string,
  RESET_USER_BUTTON: import.meta.env.VITE_APP_RESET_USER_BUTTON === "true",
  TAAS_ADVANCED_CONFIGS: clean(
    `${import.meta.env.VITE_APP_FEATUREFLAG_TAAS_ADVANCED_CONFIGS}`.split(",")
  ),
  CONTACT_US_URL: import.meta.env.VITE_APP_CONTACT_US_URL as string,
  INTELLIGENCE_PROFESSIONAL_PRICE:
    (import.meta.env.VITE_APP_INTELLIGENCE_PROFESSIONAL_PRICE as string) || "13.99",
  INTELLIGENCE_DEVELOPER_PRICE:
    (import.meta.env.VITE_APP_INTELLIGENCE_DEVELOPER_PRICE as string) || "135",
  INTELLIGENCE_PROFESSIONAL_DISCOUNT:
    parseInt(import.meta.env.VITE_APP_INTELLIGENCE_PROFESSIONAL_DISCOUNT as string) || null,
  INTELLIGENCE_DEVELOPER_DISCOUNT:
    parseInt(import.meta.env.VITE_APP_INTELLIGENCE_DEVELOPER_DISCOUNT as string) || null,
  TEXT_TO_SPEECH_MODEL:
    (import.meta.env.VITE_APP_TEXT_TO_SPEECH_MODEL as string) ||
    "speaches-ai/Kokoro-82M-v1.0-ONNX-fp16",
  SPEECH_RECOGNITION_MODEL:
    (import.meta.env.VITE_APP_SPEECH_RECOGNITION_MODEL as string) ||
    "deepdml/faster-whisper-large-v3-turbo-ct2",
  FLASH_SALE_GPUS: clean(`${import.meta.env.VITE_APP_FLASH_SALE_GPUS}`.split(",")),
  API_FUNCTION_PREFIX: import.meta.env.VITE_APP_API_FUNCTION_PREFIX ?? "",
  GA_MEASUREMENT_ID: import.meta.env.VITE_APP_GA_MEASUREMENT_ID ?? "",
  INTELLIGENCE_PROMOTION_ENABLED:
    import.meta.env.VITE_APP_INTELLIGENCE_PROMOTION_ENABLED === "true",
  AUTH0_RECAPTCHA_SITE_KEY: import.meta.env.VITE_APP_AUTH0_RECAPTCHA_SITE_KEY,
  CHAT_PREFILL_SECURE: import.meta.env.VITE_APP_CHAT_PREFILL_SECURE === "true",
  EXPLORER_COMBINED: import.meta.env.VITE_APP_EXPLORER_COMBINED === "true",
  THEME_TOGGLE_ENABLED: import.meta.env.VITE_APP_THEME_TOGGLE_ENABLED !== "false",
  LIGHT_MODE_ENABLED: import.meta.env.VITE_APP_LIGHT_MODE_ENABLED !== "false",
  SECTION_THEME_ENABLED: import.meta.env.VITE_APP_SECTION_THEME_ENABLED === "true",
  AMPLITUDE_EXPERIMENT_KEY: import.meta.env.VITE_APP_AMPLITUDE_EXPERIMENT_KEY ?? "",
  EXPLORER_ONLY_MODE: import.meta.env.VITE_APP_EXPLORER_ONLY_MODE === "true",
  /** Dev/testing: always show My Worker / My Staking dashboard menu links without API checks. */
  ALWAYS_SHOW_USER_DASHBOARD_MENUS:
    import.meta.env.VITE_APP_ALWAYS_SHOW_USER_DASHBOARD_MENUS === "true",
  /** Absolute base URL for the explorer product (e.g. https://explorer.io.net). */
  EXPLORER_DOMAIN: (import.meta.env.VITE_APP_EXPLORER_DOMAIN as string) ?? "",
  /** Absolute base URL for the cloud product (e.g. https://cloud.io.net). */
  CLOUD_DOMAIN: (import.meta.env.VITE_APP_CLOUD_DOMAIN as string) ?? "",
  /** Absolute base URL for intelligence / AI (e.g. https://ai.io.net). */
  INTELLIGENCE_DOMAIN: (import.meta.env.VITE_APP_INTELLIGENCE_DOMAIN as string) ?? "",
  FEATUREFLAG_FULL_WIDTH_TOGGLE_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_FULL_WIDTH_TOGGLE_ENABLED === "true",
  FEATUREFLAG_CLOUD_MARKETPLACE_ENABLED:
    import.meta.env.VITE_APP_FEATUREFLAG_CLOUD_MARKETPLACE_ENABLED === "true"
};

export default ENV_VARIABLES;
